import axios from '~/lib/axios'
import styled from 'styled-components'
import React, { useState, useCallback, useEffect } from 'react'
import { useBusy } from '~/lib/hooks/busy'
import { useSelector, useDispatch } from '~/lib/hooks/redux'
import { useModalShow } from '~/lib/hooks/modal'
import { useToast } from '~/lib/hooks/toast'
import { getPaymentIdKey } from '~/lib/storage'
import { getAccountData } from '~/state/ducks/accounts/selectors'
import { beginCheckoutEvent, purchaseEvent, addPaymentInfoEvent } from '~/lib/event'
import PurchaseModal from './PurchaseModal'
import CreditCardModal from './CreditCardModal'
import { ItemData, CreditCardData, PurchaseType } from '~/types'
import { PurchaseItemApiResponse, CreditCardDataApiResponse } from '~/types/api'
import { useRouter } from 'next/router'

interface Props {
  children: React.ReactNode
  item: ItemData
  type: PurchaseType
}

export default function PurchaseModalManager(props: Props) {
  const { children, item, type } = props
  const router = useRouter()
  const account = useSelector(getAccountData)
  const dispatch = useDispatch()
  const [_, setToast] = useToast()

  const [showPurchaseModal, handleClickChildren, handleClosePurchaseModal] = useModalShow()
  const [showCreditCardModal, handleShowCreditCardModal, handleCloseCreditCardModal] =
    useModalShow()

  const [purchaseNumber, setPurchaseNumber] = useState(1)
  const [creditCard, setCreditCard] = useState<CreditCardData | null>()
  const [paymentIntentId, setPaymentIntentId] = useState<string>()
  const [busy, startBusy, endBusy] = useBusy()

  const fetchCreditCard = useCallback(async () => {
    const res = await axios<CreditCardDataApiResponse>(
      {
        url: `/users/cards`
      },
      { dispatch }
    )
    setCreditCard(res.creditCard)
  }, [router])

  const fetchPreparePurchase = useCallback(async () => {
    const paymentIdKey = getPaymentIdKey(account.userId, item.itemUid, type)
    const paymentIdByStorage = sessionStorage.getItem(paymentIdKey)
    try {
      const { pId } = await axios<any>(
        {
          method: 'post',
          url: `/items/${item.itemUid}/prepare_purchase`,
          data: {
            type,
            purchaseNumber,
            paymentId: paymentIdByStorage
          }
        },
        { dispatch }
      )
      setPaymentIntentId(pId)
      // モーダル閉じて再度開いたときも使いまわしたいのでsessionStorageに書き込んでおく
      sessionStorage.setItem(paymentIdKey, pId)
    } catch {
      // エラーが起きたということはsessionStorageのpaymentIdが怪しいので削除しておく
      sessionStorage.removeItem(paymentIdKey)
    }
  }, [item, type, purchaseNumber, account])

  useEffect(() => {
    if (showPurchaseModal && account !== undefined && account !== null) {
      fetchCreditCard()
    }
  }, [showPurchaseModal, account])

  useEffect(() => {
    if (showPurchaseModal && creditCard !== null && creditCard !== undefined) {
      fetchPreparePurchase()
    }
  }, [showPurchaseModal, creditCard])

  const handleClosePurchase = useCallback(async () => {
    setPurchaseNumber(1)
    handleClosePurchaseModal()
  }, [])

  const handleEditCreditCard = useCallback(async () => {
    addPaymentInfoEvent(item, type)
    handleCloseCreditCardModal()
    handleClosePurchaseModal()
    setToast('クレジットカードを設定しました', 'Success')
    //TODO: 一旦リロードさせるがリロードしないで購入フローにイケるとおしゃれ
    router.reload()
  }, [item])

  const handleDeleteCreditCard = useCallback(async () => {
    handleCloseCreditCardModal()
    handleClosePurchaseModal()
    setToast('クレカを削除しました！', 'Success')
    //TODO: 一旦リロードさせるがリロードしないで購入フローにイケるとおしゃれ
    router.reload()
  }, [])

  const handlePurchase = useCallback(async () => {
    startBusy()
    beginCheckoutEvent(item, type)
    const refUserId = localStorage.getItem(`shared_ref_user_id_for_${item.itemUid}`)

    try {
      const res = await axios<PurchaseItemApiResponse>(
        {
          method: 'post',
          url: `/items/${item.itemUid}/purchase`,
          data: {
            type,
            purchaseNumber,
            paymentId: paymentIntentId,
            refUserId: refUserId !== null ? refUserId : undefined
          }
        },
        { dispatch }
      )

      if (res.success) {
        // 購入に成功したならsessionStorageからpaymentIdは消しておく
        const paymentIdKey = getPaymentIdKey(account.userId, item.itemUid, type)
        sessionStorage.removeItem(paymentIdKey)
        localStorage.removeItem(`shared_ref_user_id_for_${item.itemUid}`)
        purchaseEvent(item, type)
        handleClosePurchaseModal()
        endBusy()
        setToast('購入に成功しました', 'Success')
        router.reload()
      } else {
        setToast('購入に失敗しました', 'Failure')
        endBusy()
      }
    } catch (e) {
      console.error(e.code)
      console.error(e.message)
      endBusy()
    }
  }, [item, type, purchaseNumber, paymentIntentId, account])

  return (
    <>
      <ChildWrapper
        onClick={process.env.NEXT_PUBLIC_ENABLE_PURCHASE === 'true' && handleClickChildren}
      >
        {children}
      </ChildWrapper>
      <PurchaseModal
        type={type}
        show={showPurchaseModal}
        itemPrice={item.price}
        purchaseNumber={purchaseNumber}
        creditCard={creditCard}
        busy={busy}
        ready={creditCard !== undefined && creditCard !== null && paymentIntentId !== undefined}
        onClose={handleClosePurchase}
        onPurchase={handlePurchase}
        onChange={setPurchaseNumber}
        onRemove={handleDeleteCreditCard}
        onOpenCreditCardModal={handleShowCreditCardModal}
      />
      <CreditCardModal
        show={showCreditCardModal}
        creditCard={creditCard}
        onChange={handleEditCreditCard}
        onClose={handleCloseCreditCardModal}
      />
    </>
  )
}

const ChildWrapper = styled.div``
